import React from 'react';
import { Alert } from 'reactstrap';
import { isProblemDetails, isValidationProblemDetails } from '~/lib/helper/ErrorHelper';

/**
 * Renders the body of the global error modal for any error shape: a string, a
 * ValidationProblemDetails (lists every message it carries), a ProblemDetails (title + detail +
 * request id), or an opaque error (generic fallback). An optional `errorSummary` — the caller's
 * human context — is rendered as a warning lead-in above the structured detail.
 *
 * Deciding *which* messages reach the modal (vs render inline next to a field) is the caller's
 * job: form-submit errors route through `formErrorModalBody`, which strips inline-rendered field
 * errors before they get here. This renderer just lists whatever it is handed.
 */
export function errorModalBody(error: unknown, errorSummary?: string): React.ReactNode {
    // A human lead-in (the caller's errorSummary) rendered as a warning Alert above the
    // structured error detail. The ProblemDetails and unknown-error branches fall back to a
    // generic sentence; the string and validation branches have self-explanatory bodies, so
    // they show the lead-in only when an explicit errorSummary is supplied.
    const summaryAlert = (text: string) => (
        <Alert color="warning">
            <i className="ion-md-warning me-2" aria-hidden="true" />
            {text}
        </Alert>
    );

    if (typeof error === 'string') {
        return (
            <>
                {errorSummary && summaryAlert(errorSummary)}
                <p>{error}</p>
            </>
        );
    }

    if (isValidationProblemDetails(error)) {
        const allErrors = error.errors as Record<string, string[] | undefined>;
        const messages: string[] = [];
        // Flatten with a loop; Object.values()/.flatMap() aren't in the project's tsc lib target.
        for (const key of Object.keys(allErrors)) {
            const fieldMessages = allErrors[key];
            if (fieldMessages) {
                messages.push(...fieldMessages);
            }
        }
        const body = messages.length ? (
            <ul className="mb-0">
                {messages.map((m, i) => (
                    <li key={i}>{m}</li>
                ))}
            </ul>
        ) : (
            <p>
                {error.title}
                {error.detail ? ` - ${error.detail}` : ''}
            </p>
        );
        return (
            <>
                {errorSummary && summaryAlert(errorSummary)}
                {body}
            </>
        );
    }

    if (!isProblemDetails(error)) {
        const message = error instanceof Error && error.message ? `: ${error.message}` : '';
        return summaryAlert(errorSummary ?? `We have encountered an error trying to process your request${message}.`);
    }

    return (
        <>
            {summaryAlert(errorSummary ?? 'We have encountered an error trying to process your request:')}
            <p>
                <strong>{error.title}</strong>
            </p>
            {error.detail && <p>{error.detail}</p>}
            {error.request_id_message && <p>{error.request_id_message}</p>}
        </>
    );
}
